import { createElement, Fragment, type ReactNode } from 'react';
import { formatNumber, formatValue, toDate } from './format';
import type { AppGridColumn, GridKey, GridSummaryItem, SummaryType } from './types';

/* ------------------------------------------------------------------ */
/*  Columns                                                            */
/* ------------------------------------------------------------------ */

export function columnId<T>(c: AppGridColumn<T>, index: number): string {
  return c.name ?? c.dataField ?? `col${index}`;
}

export function columnCaption<T>(c: AppGridColumn<T>): string {
  if (c.caption != null) return c.caption;
  const f = c.dataField ?? c.name ?? '';
  // DevExtreme's default caption: "fKey_Doctor" -> "F Key Doctor".
  return f.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (s) => s.toUpperCase());
}

export function isNumericColumn<T>(c: AppGridColumn<T>): boolean {
  return c.dataType === 'number';
}

/** Field names that hold a picture or an icon, not text anyone would filter by. */
const IMAGE_FIELD = /image|photo|picture|avatar|icon|logo|thumb/i;

/**
 * Whether a column gets a header filter button when the grid's headerFilter
 * is visible (the default). An explicit allowHeaderFiltering / allowFiltering
 * wins. Otherwise ON for columns that show text, numbers or dates, and OFF for
 * columns a list of values means nothing for:
 *   - render-only columns (no dataField / calculateCellValue): action buttons, icons
 *   - columns with no caption (icon columns)
 *   - checkbox columns: dataType 'boolean', or values that are booleans (`boolValued`)
 *   - image columns (the field name says image / photo / icon ...)
 */
export function headerFilterable<T>(c: AppGridColumn<T>, boolValued: boolean): boolean {
  if (c.allowFiltering === false) return false;
  if (c.allowHeaderFiltering != null) return c.allowHeaderFiltering;
  if (!c.dataField && !c.calculateCellValue) return false;
  if (!columnCaption(c).trim()) return false;
  if (c.dataType === 'boolean' || boolValued) return false;
  if (IMAGE_FIELD.test(c.dataField ?? c.name ?? '')) return false;
  return true;
}

/** Raw cell value: calculateCellValue, else row[dataField]. */
export function cellValue<T>(c: AppGridColumn<T>, row: T): unknown {
  if (c.calculateCellValue) return c.calculateCellValue(row);
  if (c.dataField) return (row as Record<string, unknown>)[c.dataField];
  return undefined;
}

export function lookupText<T>(c: AppGridColumn<T>, value: unknown): unknown {
  if (!c.lookup || value == null) return value;
  const item = c.lookup.dataSource.find((x) => (x as Record<string, unknown>)[c.lookup!.valueExpr] === value);
  return item ? (item as Record<string, unknown>)[c.lookup.displayExpr] : value;
}

/**
 * The text a column shows for a row: its calculateDisplayValue, else the
 * value's own text (cellTextOf). What the search, the header filter, grouping
 * and the exports compare and write.
 */
export function shownTextOf<T>(c: AppGridColumn<T>, row: T): string {
  if (c.calculateDisplayValue) return c.calculateDisplayValue(row) ?? '';
  return cellTextOf(c, cellValue(c, row));
}

/** The text a plain cell shows: lookup, format, then customizeText. */
export function cellTextOf<T>(c: AppGridColumn<T>, value: unknown): string {
  const shown = lookupText(c, value);
  const valueText = c.lookup ? (shown == null ? '' : String(shown)) : formatValue(shown, c.format, c.dataType);
  return c.customizeText ? c.customizeText({ value, valueText }) : valueText;
}

/**
 * Display text per row per column, cached for the life of one column set.
 * Search, header filter and highlight all read the SAME text the cell shows,
 * which is what makes "10" match the "10" inside "103-B" and nothing hidden.
 *
 * Columns are read through `col`, so text worked out later uses the screen's
 * latest columns. Text already cached can go stale when a calculateCellValue or
 * customizeText closes over a list that loads after the rows; `isStale` tells.
 */
export class TextCache<T> {
  private map = new WeakMap<object, Map<string, string>>();
  private col: (id: string) => AppGridColumn<T> | undefined;
  constructor(col: (id: string) => AppGridColumn<T> | undefined) {
    this.col = col;
  }
  private compute(row: T, colId: string): string {
    const c = this.col(colId);
    return c ? shownTextOf(c, row) : '';
  }
  get(row: T, colId: string): string {
    const r = row as unknown as object;
    let m = this.map.get(r);
    if (!m) {
      m = new Map();
      this.map.set(r, m);
    }
    let s = m.get(colId);
    if (s === undefined) {
      s = this.compute(row, colId);
      m.set(colId, s);
    }
    return s;
  }
  /** True when any text cached for `rows` differs from what the columns give now. */
  isStale(rows: readonly T[]): boolean {
    for (const row of rows) {
      const m = this.map.get(row as unknown as object);
      if (!m) continue;
      for (const [colId, s] of m) {
        if (this.compute(row, colId) !== s) return true;
      }
    }
    return false;
  }
}

/* ------------------------------------------------------------------ */
/*  Sorting                                                            */
/* ------------------------------------------------------------------ */

const collator = new Intl.Collator('en-US', { sensitivity: 'base', numeric: false });

export function sortValueOf<T>(c: AppGridColumn<T>, row: T): unknown {
  if (c.calculateSortValue) return c.calculateSortValue(row);
  const v = cellValue(c, row);
  // Lookup columns sort by what they show, as DevExtreme does.
  if (c.lookup) return lookupText(c, v);
  if (c.dataType === 'date' || c.dataType === 'datetime') return toDate(v)?.getTime() ?? null;
  return v;
}

/** Ascending compare; blanks first (DevExtreme order). */
export function compareValues(a: unknown, b: unknown): number {
  const ea = a == null || a === '';
  const eb = b == null || b === '';
  if (ea || eb) return ea === eb ? 0 : ea ? -1 : 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? 1 : -1;
  return collator.compare(String(a), String(b));
}

/* ------------------------------------------------------------------ */
/*  Search highlight                                                   */
/* ------------------------------------------------------------------ */

/**
 * Case-insensitive matches of `query` in `text` wrapped in <mark class="app-grid-hl">.
 * Several queries (the search panel's and `highlightText`) mark every match of any
 * of them; where two overlap, the earlier (then the longer) match wins.
 */
export function highlightText(text: string | null | undefined, query: string | readonly string[]): ReactNode {
  if (text == null || text === '') return text ?? '';
  const qs = (typeof query === 'string' ? [query] : query).filter(Boolean).map((q) => q.toLowerCase());
  if (!qs.length) return text;
  const lower = text.toLowerCase();
  const next = (from: number): [number, number] => {
    let best = -1;
    let len = 0;
    for (const q of qs) {
      const at = lower.indexOf(q, from);
      if (at >= 0 && (best < 0 || at < best || (at === best && q.length > len))) {
        best = at;
        len = q.length;
      }
    }
    return [best, len];
  };
  let [idx, len] = next(0);
  if (idx < 0) return text;
  const parts: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (idx >= 0) {
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(createElement('mark', { key: k++, className: 'app-grid-hl' }, text.slice(idx, idx + len)));
    i = idx + len;
    [idx, len] = next(i);
  }
  if (i < text.length) parts.push(text.slice(i));
  return createElement(Fragment, null, ...parts);
}

/** True when the text in cell content `cc` (or an element between `from` and `cc`) is cut off. */
export function isTruncated(cc: HTMLElement, from: HTMLElement | null): boolean {
  if (cc.scrollWidth > cc.clientWidth + 1) return true;
  for (let el = from; el && el !== cc && cc.contains(el); el = el.parentElement) {
    if (el.clientWidth > 0 && el.scrollWidth > el.clientWidth + 1) return true;
  }
  return false;
}

/** Shown text of a group value (calculateGroupValue's result). */
export function groupValueText<T>(c: AppGridColumn<T>, value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  return cellTextOf({ ...c, customizeText: undefined }, value);
}

/* ------------------------------------------------------------------ */
/*  Summaries                                                          */
/* ------------------------------------------------------------------ */

export function computeSummary<T>(type: SummaryType, rows: readonly T[], c: AppGridColumn<T> | undefined): number | null {
  if (type === 'count') return rows.length;
  if (!c) return null;
  let n = 0;
  let sum = 0;
  let min = Infinity;
  let max = -Infinity;
  for (const r of rows) {
    const raw = cellValue(c, r);
    const v = typeof raw === 'number' ? raw : raw instanceof Date ? raw.getTime() : raw == null || raw === '' ? NaN : Number(raw);
    if (Number.isNaN(v)) continue;
    n++;
    sum += v;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (n === 0) return null;
  switch (type) {
    case 'sum': return sum;
    case 'avg': return sum / n;
    case 'min': return min;
    case 'max': return max;
  }
}

const DEFAULT_DISPLAY: Record<SummaryType, string> = {
  sum: 'Sum: {0}',
  avg: 'Avg: {0}',
  count: 'Count: {0}',
  min: 'Min: {0}',
  max: 'Max: {0}',
};

export function summaryText<T>(item: GridSummaryItem, value: number | null, c: AppGridColumn<T> | undefined): string {
  if (value == null) return '';
  const fmt = item.valueFormat ?? (item.summaryType === 'count' ? undefined : c?.format);
  const valueText = item.summaryType === 'count' ? formatNumber(value, '#,##0') : formatValue(value, fmt, c?.dataType === 'date' ? 'date' : 'number');
  if (item.customizeText) return item.customizeText({ value, valueText });
  const tpl = item.displayFormat ?? DEFAULT_DISPLAY[item.summaryType];
  return tpl.replace('{0}', valueText).replace('{1}', c ? columnCaption(c) : '');
}

/* ------------------------------------------------------------------ */
/*  Keys                                                               */
/* ------------------------------------------------------------------ */

export function keyGetter<T>(keyExpr: string | ((row: T) => GridKey)): (row: T) => GridKey {
  return typeof keyExpr === 'function' ? keyExpr : (row: T) => (row as Record<string, unknown>)[keyExpr] as GridKey;
}

/** Header text width in px, for auto-sizing so captions are never cut ("R."). */
let measureCtx: CanvasRenderingContext2D | null | undefined;
export function measureText(text: string, font: string): number {
  if (measureCtx === undefined) {
    try {
      measureCtx = typeof document !== 'undefined' && !/jsdom/i.test(navigator.userAgent) ? document.createElement('canvas').getContext('2d') : null;
    } catch {
      measureCtx = null;
    }
  }
  if (!measureCtx) return text.length * 7.5;
  measureCtx.font = font;
  return measureCtx.measureText(text).width;
}
